import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import BaseDashboard from '../components/BaseDashboard';
import { createApiUrl } from '../utils/apiConfig';
import AllUsers from '../components/AllUsers';
import AllOrgs from '../components/AllOrgs';
import AllSchoolAdmins from '../components/AllSchoolAdmins';
import AllStudents from '../components/AllStudents';
import AllPlans from '../components/AllPlans';
import AllAssets from '../components/AllAssets';
import UploadAsset from '../components/UploadAsset';
import UploadWebData from '../components/UploadWebData';
import LogoUpload from '../components/LogoUpload';

const AdminDashboard = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [schoolAdmins, setSchoolAdmins] = useState([]);
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [assets, setAssets] = useState([]);
  const [webData, setWebData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgCurrentPage, setOrgCurrentPage] = useState(1);
  const [_orgTotalPages, setOrgTotalPages] = useState(1);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [schoolAdminCurrentPage, setSchoolAdminCurrentPage] = useState(1);
  const [schoolAdminTotalPages, setSchoolAdminTotalPages] = useState(1);
  const [initialLoad, setInitialLoad] = useState(true);
  const [schoolAdminSearchQuery, setSchoolAdminSearchQuery] = useState('');
  const [studentCurrentPage, setStudentCurrentPage] = useState(1);
  const [_studentTotalPages, setStudentTotalPages] = useState(1);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Items per page state for each tab
  const [usersItemsPerPage, setUsersItemsPerPage] = useState(12);
  const [orgsItemsPerPage, setOrgsItemsPerPage] = useState(12);
  const [schoolAdminsItemsPerPage, setSchoolAdminsItemsPerPage] = useState(10);
  const [studentsItemsPerPage, setStudentsItemsPerPage] = useState(10);

  const tabs = [
    { id: 'users', label: 'All Users' },
    { id: 'orgs', label: 'All Orgs' },
    { id: 'schoolAdmins', label: 'Sch Admins' },
    { id: 'students', label: 'Students' },
    { id: 'plans', label: 'All Plans' },
    { id: 'assets', label: 'Manage Assets' },
    { id: 'upload', label: 'Create Asset' },
    { id: 'uploadData', label: 'Upload WebData' },
    { id: 'logoUpload', label: 'Partner Logos' },
    { id: 'newsAdmin', label: 'News Admin' },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(createApiUrl('/api/dashboard'), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      
      const { user, users, organisations, schoolAdmins, students, plans, assets, webData, currentPage, totalPages, orgCurrentPage, orgTotalPages, schoolAdminCurrentPage, schoolAdminTotalPages, studentCurrentPage, studentTotalPages } = response.data;
      
      setUser(user);
      // Filter out the other site admin user
      const filteredUsers = (users || []).filter(user => user.email !== 'info@kidsblackhistory.com');
      setUsers(filteredUsers);
      setOrganisations(organisations || []);
      setSchoolAdmins(schoolAdmins || []);
      setStudents(students || []);
      setPlans(plans || []);
      setAssets(assets || []);
      setWebData(webData || []);
      setCurrentPage(currentPage || 1);
      setTotalPages(totalPages || 1);
      setOrgCurrentPage(orgCurrentPage || 1);
      setOrgTotalPages(orgTotalPages || 1);
      setSchoolAdminCurrentPage(schoolAdminCurrentPage || 1);
      setSchoolAdminTotalPages(schoolAdminTotalPages || 1);
      setStudentCurrentPage(studentCurrentPage || 1);
      setStudentTotalPages(studentTotalPages || 1);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
      setLoading(false);
      
      // Set fallback data to prevent complete failure
      setUsers([]);
      setOrganisations([]);
      setSchoolAdmins([]);
      setStudents([]);
      setPlans([]);
      setAssets([]);
      setWebData([]);
    }
  };

  const fetchSchoolAdminData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(createApiUrl(`/api/dashboard?page=${schoolAdminCurrentPage}&tab=schoolAdmins`), {
        headers: {
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      const { schoolAdmins, schoolAdminCurrentPage: newCurrentPage, schoolAdminTotalPages: newTotalPages } = response.data;
      setSchoolAdmins(schoolAdmins || []);
      setSchoolAdminCurrentPage(newCurrentPage || schoolAdminCurrentPage);
      setSchoolAdminTotalPages(newTotalPages || schoolAdminTotalPages);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching school admin data:', error);
      setError(error.response?.data?.message || 'Failed to load school admin data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setInitialLoad(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Fetch school admin data when page changes
  useEffect(() => {
    if (!initialLoad) {
      fetchSchoolAdminData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSchoolAdminData/initialLoad intentionally excluded
  }, [schoolAdminCurrentPage]);

  // Reset pagination when search queries change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setOrgCurrentPage(1);
  }, [orgSearchQuery]);

  useEffect(() => {
    setSchoolAdminCurrentPage(1);
  }, [schoolAdminSearchQuery]);

  useEffect(() => {
    setStudentCurrentPage(1);
  }, [studentSearchQuery]);

  // Filter functions for search
  const getFilteredUsers = () => {
    if (!searchQuery.trim()) return users;
    return users.filter(user => 
      user.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.schoolName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.postCode?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getFilteredOrgs = () => {
    if (!orgSearchQuery.trim()) return organisations;
    return organisations.filter(org => 
      org.name?.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
      org.contactName?.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
      org.email?.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
      org.code?.toLowerCase().includes(orgSearchQuery.toLowerCase()) ||
      org.postCode?.toLowerCase().includes(orgSearchQuery.toLowerCase())
    );
  };

  const getFilteredSchoolAdmins = () => {
    if (!schoolAdminSearchQuery.trim()) return schoolAdmins;
    return schoolAdmins.filter(admin => 
      admin.contactName?.toLowerCase().includes(schoolAdminSearchQuery.toLowerCase()) ||
      admin.email?.toLowerCase().includes(schoolAdminSearchQuery.toLowerCase()) ||
      admin.schoolName?.toLowerCase().includes(schoolAdminSearchQuery.toLowerCase()) ||
      admin.schoolCode?.toLowerCase().includes(schoolAdminSearchQuery.toLowerCase())
    );
  };

  const getFilteredStudents = () => {
    if (!studentSearchQuery.trim()) return students;
    return students.filter(student => 
      student.contactName?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.firstName?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.lastName?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.email?.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
      student.studentCode?.toLowerCase().includes(studentSearchQuery.toLowerCase())
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleOrgSearch = (e) => {
    e.preventDefault();
    setOrgCurrentPage(1);
  };

  const handleOrgPageChange = (page) => {
    setOrgCurrentPage(page);
  };

  const handleSchoolAdminSearch = (e) => {
    e.preventDefault();
    setSchoolAdminCurrentPage(1);
  };

  const handleSchoolAdminPageChange = (page) => {
    setSchoolAdminCurrentPage(page);
  };

  const handleStudentSearch = (e) => {
    e.preventDefault();
    setStudentCurrentPage(1);
  };

  const handleStudentPageChange = (page) => {
    setStudentCurrentPage(page);
  };

  // Pagination functions for filtered results
  const getPaginatedUsers = () => {
    const filtered = getFilteredUsers();
    const startIndex = (currentPage - 1) * usersItemsPerPage;
    const endIndex = startIndex + usersItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getPaginatedOrgs = () => {
    const filtered = getFilteredOrgs();
    const startIndex = (orgCurrentPage - 1) * orgsItemsPerPage;
    const endIndex = startIndex + orgsItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getPaginatedSchoolAdmins = () => {
    const filtered = getFilteredSchoolAdmins();
    const startIndex = (schoolAdminCurrentPage - 1) * schoolAdminsItemsPerPage;
    const endIndex = startIndex + schoolAdminsItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getPaginatedStudents = () => {
    const filtered = getFilteredStudents();
    const startIndex = (studentCurrentPage - 1) * studentsItemsPerPage;
    const endIndex = startIndex + studentsItemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  if (error) {
    return (
      <BaseDashboard
        title="Admin Dashboard"
        tabs={tabs}
        user={user}
        loading={false}
      >
        <div className="error-message">
          <div className="alert alert-danger">
            <h4>Error Loading Dashboard</h4>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchData}>
              Retry
            </button>
          </div>
        </div>
      </BaseDashboard>
    );
  }

  return (
    <div className="admin-dashboard-wrapper">
      <BaseDashboard
        title="Admin Dashboard"
        tabs={tabs}
        user={user}
        loading={loading}
        defaultActiveTab={location.state?.activeTab}
      >
      {/* Users Tab */}
      <div tabId="users" className="users-tab-content">
        <AllUsers 
          users={getPaginatedUsers()}
          currentPage={currentPage}
          totalPages={Math.ceil(getFilteredUsers().length / usersItemsPerPage)}
          searchQuery={searchQuery}
          handleSearch={handleSearch}
          handlePageChange={handlePageChange}
          setSearchQuery={setSearchQuery}
          itemsPerPage={usersItemsPerPage}
          onItemsPerPageChange={setUsersItemsPerPage}
          totalItems={getFilteredUsers().length}
        />
      </div>

      {/* Organizations Tab */}
      <div tabId="orgs" className="orgs-tab-content">
            <AllOrgs 
              organisations={getPaginatedOrgs()}
              currentPage={orgCurrentPage}
              totalPages={Math.ceil(getFilteredOrgs().length / orgsItemsPerPage)}
              searchQuery={orgSearchQuery}
              handleSearch={handleOrgSearch}
              handlePageChange={handleOrgPageChange}
              setSearchQuery={setOrgSearchQuery}
              itemsPerPage={orgsItemsPerPage}
              onItemsPerPageChange={setOrgsItemsPerPage}
              totalItems={getFilteredOrgs().length}
            />
            </div>

      {/* School Admins Tab */}
      <div tabId="schoolAdmins" className="school-admins-tab-content">
        <AllSchoolAdmins 
          schoolAdmins={getPaginatedSchoolAdmins()}
          currentPage={schoolAdminCurrentPage}
          totalPages={Math.ceil(getFilteredSchoolAdmins().length / schoolAdminsItemsPerPage)}
          searchQuery={schoolAdminSearchQuery}
          handleSearch={handleSchoolAdminSearch}
          handlePageChange={handleSchoolAdminPageChange}
          setSearchQuery={setSchoolAdminSearchQuery}
          itemsPerPage={schoolAdminsItemsPerPage}
          onItemsPerPageChange={setSchoolAdminsItemsPerPage}
          totalItems={getFilteredSchoolAdmins().length}
        />
      </div>

      {/* Students Tab */}
      <div tabId="students" className="students-tab-content">
        <AllStudents 
          students={getPaginatedStudents()}
          currentPage={studentCurrentPage}
          totalPages={Math.ceil(getFilteredStudents().length / studentsItemsPerPage)}
          searchQuery={studentSearchQuery}
          handleSearch={handleStudentSearch}
          handlePageChange={handleStudentPageChange}
          setSearchQuery={setStudentSearchQuery}
          itemsPerPage={studentsItemsPerPage}
          onItemsPerPageChange={setStudentsItemsPerPage}
          totalItems={getFilteredStudents().length}
        />
      </div>

      {/* Plans Tab */}
      <div tabId="plans" className="plans-tab-content">
        <AllPlans 
          plans={plans}
          assets={assets}
          refetchData={fetchData}
        />
        </div>

      {/* Manage Assets Tab */}
      <div tabId="assets" className="assets-tab-content">
        <AllAssets 
          assets={assets}
          refetchData={fetchData}
        />
      </div>

      {/* Create Asset Tab */}
      <div tabId="upload" className="upload-tab-content">
        <UploadAsset 
          assets={assets}
          refetchData={fetchData}
        />
      </div>

      {/* Upload WebData Tab */}
      <div tabId="uploadData" className="upload-data-tab-content">
        <UploadWebData 
          webData={webData}
          refetchData={fetchData}
        />
      </div>

      {/* Logo Upload Tab */}
      <div tabId="logoUpload" className="logo-upload-tab-content">
        <LogoUpload />
      </div>

      {/* News Admin Tab */}
      <div tabId="newsAdmin" className="news-admin-tab-content" style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>News Content Management</h2>
        <p>Manage news articles, announcements, and updates for the platform.</p>
        <a href="/news-admin" className="btn btn-primary" style={{ fontSize: '1.2rem', padding: '0.75rem 2rem', marginTop: '1rem' }}>
          Go to News Admin
        </a>
      </div>
    </BaseDashboard>
    </div>
  );
};

export default AdminDashboard; 